/**
 * <zg-price> and <zg-purchase-options>: the storefront v3 price row and purchase options
 * (blocks/zg-price.liquid, blocks/zg-purchase-options.liquid).
 *
 * Both blocks render their starting state in Liquid plus the product's variant and selling-plan data
 * (snippets/zg-product-data.liquid). Horizon does not re-render them when the shopper changes variant
 * (variant-picker.js morphs only its own markup), so they follow `shopify:product:select` themselves:
 * the variant id is known synchronously (event.detail.variantId) and confirmed when event.promise resolves.
 *
 * <zg-purchase-options> owns the hidden `selling_plan` input attached to the buy buttons' product form,
 * hides plan rows the variant has no allocation for (moving a selection there to the new variant's plan row with the
 * same group name or position, else to one-time purchase), keeps
 * ?selling_plan= in the URL in step, and dispatches `zg:purchase-option-change`
 * {variantId, sellingPlanId | null} (bubbling) on connect and whenever the variant, mode or plan changes.
 * The first plan row also follows the variant's item count: its plan_text_by_units line replaces its title
 * and description. Each plan row shows its saving (data-show-savings): the struck price and a "Save 10%" badge.
 *
 * <zg-price> shows the active price (plan allocation or variant price), the struck price, a "Save 10%" badge for a
 * plan (data-show-plan-savings), the suffix with the price per item, and the variant's comparison line. The suffix is
 * the price_suffix_by_units line for the variant's item count, else price_suffix. It also sets --zg-atc-total
 * (" · $71.10", or "" when the variant can't be bought) on .zg-product-information for the add to cart button's
 * ::after.
 *
 * The item count is the variant's `units` in the product data (snippets/zg-product-data.liquid, field 'units': its
 * unit price count, custom.zg_units_per_pack metafield or pack option). Both badges read their text from
 * data-save-template ("Save [percent]%") and compute the percent as the Liquid does (savePercent below).
 *
 * Both *_by_units settings are read on the server by snippets/zg-text-by-units.liquid, which also renders their
 * lines as JSON for textForUnits() below, so server and script pick the same line.
 *
 * Elsewhere Horizon does morph these elements in place, which doesn't reconnect them. A featured product section
 * is morphed back to its server state on every variant change, and a combined listing or a reused quick add
 * dialog morphs them into another product's. So both elements re-read their data when a selection settles or
 * the product id changes. <zg-purchase-options> then puts the shopper's choice back.
 */
import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

export const PURCHASE_OPTION_CHANGE = 'zg:purchase-option-change';

/**
 * @typedef {object} AllocationData
 * @property {number} sellingPlanId
 * @property {string} groupId
 * @property {number} price
 * @property {number | null} compareAtPrice
 *
 * @typedef {object} VariantData
 * @property {number} id
 * @property {boolean} available
 * @property {number} price
 * @property {number | null} compareAtPrice
 * @property {number | null} units
 * @property {string | null} comparison
 * @property {AllocationData[]} allocations
 *
 * @typedef {object} PlanData
 * @property {number} id
 * @property {string} name
 * @property {string | null} description
 * @property {{type: string, value: number} | null} adjustment
 *
 * @typedef {object} GroupData
 * @property {string} id
 * @property {string} name
 * @property {PlanData[]} plans
 *
 * @typedef {object} ProductDataJson
 * @property {number} productId
 * @property {boolean} requiresSellingPlan
 * @property {string} moneyFormat
 * @property {string} currency
 * @property {VariantData[]} variants
 * @property {GroupData[]} groups
 */

/** Read-only view of the JSON rendered by snippets/zg-product-data.liquid. */
class ProductData {
  /** @param {ProductDataJson} json */
  constructor(json) {
    this.productId = json.productId == null ? '' : String(json.productId);
    this.requiresSellingPlan = Boolean(json.requiresSellingPlan);
    this.moneyFormat = decodeEntities(json.moneyFormat || '{{amount}}');
    this.currency = json.currency || 'CAD';
    this.variants = Array.isArray(json.variants) ? json.variants : [];
    this.groups = Array.isArray(json.groups) ? json.groups : [];
  }

  /**
   * @param {Element} element
   * @returns {ProductData | null}
   */
  static from(element) {
    const script = element.querySelector('script[data-zg-product-data]');
    if (!script?.textContent) return null;
    try {
      return new ProductData(JSON.parse(script.textContent));
    } catch (error) {
      console.warn('[zg-purchase] Unreadable product data', error);
      return null;
    }
  }

  /** @param {string | number | null | undefined} id */
  variant(id) {
    if (id == null || id === '') return null;
    return this.variants.find((variant) => String(variant.id) === String(id)) ?? null;
  }

  /** @param {string | number | null | undefined} id */
  group(id) {
    if (id == null) return null;
    return this.groups.find((group) => String(group.id) === String(id)) ?? null;
  }

  /** @param {string | number | null | undefined} id */
  plan(id) {
    if (id == null || id === '') return null;
    for (const group of this.groups) {
      const plan = group.plans.find((item) => String(item.id) === String(id));
      if (plan) return plan;
    }
    return null;
  }

  /**
   * @param {VariantData | null} variant
   * @param {string | number | null | undefined} planId
   */
  allocation(variant, planId) {
    if (!variant || planId == null || planId === '') return null;
    return variant.allocations.find((allocation) => String(allocation.sellingPlanId) === String(planId)) ?? null;
  }

  /**
   * The price a shopper pays for one unit, and the price to strike through (null when there's none):
   * with a plan, the allocation price against the one-time price; without, the variant price against its
   * compare-at price. A plan the variant has no allocation for counts as one-time purchase. `isPlan` tells
   * whether the price comes from a plan allocation.
   * @param {VariantData} variant
   * @param {string | null} planId
   * @returns {{price: number, compareAt: number | null, isPlan: boolean}}
   */
  pricing(variant, planId) {
    const allocation = this.allocation(variant, planId);
    if (allocation) {
      const compareAt = allocation.compareAtPrice ?? variant.price;
      return { price: allocation.price, compareAt: compareAt > allocation.price ? compareAt : null, isPlan: true };
    }
    const compareAt = variant.compareAtPrice;
    return {
      price: variant.price,
      compareAt: compareAt != null && compareAt > variant.price ? compareAt : null,
      isPlan: false,
    };
  }

  /** @param {number} cents */
  money(cents) {
    return formatMoney(Math.round(cents), this.moneyFormat, this.currency);
  }
}

/**
 * A money format may hold HTML entities (strip_html keeps them); the JSON doesn't decode them.
 * @param {string} text
 */
function decodeEntities(text) {
  if (!text.includes('&')) return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * The element Horizon dispatches product events in: the section, or a quick-add dialog.
 * @param {Element} element
 * @returns {Element | Document}
 */
function scopeOf(element) {
  return element.closest('.shopify-section, dialog') ?? document;
}

/**
 * Whether a product:select event comes from this product's own picker (not a product card).
 * @param {Event} event
 * @param {string | undefined} productId
 */
function isOwnProductSelect(event, productId) {
  const { target } = event;
  if (!(target instanceof Element) || target.closest('product-card')) return false;
  const source = target.closest('[data-product-id]')?.getAttribute('data-product-id');
  return !source || source === productId;
}

/**
 * Runs `onResolve` when the event's promise settles; superseded selections reject with AbortError.
 * @param {Event & {promise?: Promise<any>}} event
 * @param {(result: any) => void} onResolve
 */
function whenSettled(event, onResolve) {
  event.promise?.then(onResolve).catch((error) => {
    if (error?.name !== 'AbortError') console.warn('[zg-purchase] Product select failed', error);
  });
}

/**
 * @param {Element | null | undefined} element
 * @param {string} text
 */
function setText(element, text) {
  if (element && element.textContent !== text) element.textContent = text;
}

/**
 * @param {string | undefined} text
 * @returns {any} The parsed JSON, or null when there is none or it can't be read.
 */
function parseJson(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (error) {
    console.warn('[zg-purchase] Unreadable JSON', error);
    return null;
  }
}

/**
 * The text of the "<units>: <text>" setting line for a variant's item count, as snippets/zg-text-by-units.liquid
 * picks it on the server: the first entry whose units equal `units`, else ''.
 * @param {unknown} entries - The snippet's field 'json' output, parsed: [{units, text}]
 * @param {number | null | undefined} units
 * @returns {string}
 */
function textForUnits(entries, units) {
  if (!Array.isArray(entries) || units == null) return '';
  const entry = entries.find((item) => item != null && Number(item.units) === Number(units));
  return typeof entry?.text === 'string' ? entry.text : '';
}

/**
 * A plan row's title and description for the variant's item count, from its data-zg-plan-text (first plan row, only
 * with plan_text_by_units lines; blocks/zg-purchase-options.liquid). The variant's "<title> | <description>" line is
 * split at the first | and each part trimmed, as in the Liquid; a blank part keeps the row's own. A null description
 * means the selected plan's own. Null for a row without data-zg-plan-text.
 * @param {HTMLElement} row
 * @param {VariantData | null} variant
 * @returns {{title: string, description: string | null} | null}
 */
function planTextFor(row, variant) {
  const config = parseJson(row.dataset.zgPlanText);
  if (!config || typeof config !== 'object') return null;

  const text = textForUnits(config.lines, variant?.units);
  const bar = text.indexOf('|');
  const title = (bar < 0 ? text : text.slice(0, bar)).trim();
  const description = bar < 0 ? '' : text.slice(bar + 1).trim();
  return {
    title: title || (typeof config.title === 'string' ? config.title : ''),
    description: description || (typeof config.description === 'string' ? config.description : null),
  };
}

/**
 * The saving as a whole percent of the struck price, rounded half up: round((compareAt − price) × 100 ÷ compareAt),
 * in whole numbers so it equals the Liquid in blocks/zg-price.liquid and blocks/zg-purchase-options.liquid.
 * 0 without a saving.
 * @param {number} price - In cents
 * @param {number | null} compareAt - In cents
 * @returns {number}
 */
function savePercent(price, compareAt) {
  if (compareAt == null || !(compareAt > price)) return 0;
  return Math.floor(((compareAt - price) * 200 + compareAt) / (compareAt * 2));
}

/**
 * A savings badge's text: the element's data-save-template ("Save [percent]%") with the percent in, or '' when there
 * is no template or no whole percent saved.
 * @param {string | undefined} template
 * @param {number} percent
 * @returns {string}
 */
function saveText(template, percent) {
  return template && percent > 0 ? template.replaceAll('[percent]', String(percent)) : '';
}

/** @param {string} text */
function cssString(text) {
  return `"${text.replace(/[\\"]/g, '\\$&').replace(/\n/g, '\\a ')}"`;
}

/**
 * Whether `data` still belongs to the product `element` shows; a morph into another product changes the latter.
 * @param {HTMLElement} element
 * @param {ProductData | null} data
 */
function isCurrent(element, data) {
  return data != null && data.productId === (element.dataset.productId ?? '');
}

class ZgPurchaseOptions extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {ProductData | null} */
  #data = null;
  #variantId = '';
  #generation = 0;
  /** The last state dispatched, `variantId:planId`. */
  #emitted = '';
  /**
   * The shopper's choice as of the last update (checked radio's value, each frequency select's value by group id),
   * put back after Horizon morphs this element to its server state.
   * @type {{radio: string | null, selects: Map<string, string>}}
   */
  #choice = { radio: null, selects: new Map() };

  /** The selected plan id, or null for one-time purchase. */
  get sellingPlanId() {
    const input = this.#input;
    return input && !input.disabled && input.value ? input.value : null;
  }

  get variantId() {
    return this.#variantId;
  }

  connectedCallback() {
    this.#data = ProductData.from(this);
    if (!this.#data) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#variantId = this.dataset.variantId ?? '';

    this.addEventListener('change', this.#onChange, { signal });
    scopeOf(this).addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // The server rendered the starting state; this also settles form state a browser restored on Back.
    this.#applyVariant();
    this.#update({ url: 'present', emit: false });
    // After the rest of the section has connected, so zg-price (wherever it sits) hears it.
    queueMicrotask(() => this.#emit(true));
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get #input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-zg-selling-plan]'));
  }

  get #rows() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-zg-row]')]);
  }

  get #radios() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('[data-zg-row] input[type="radio"]')]);
  }

  get #selects() {
    return /** @type {HTMLSelectElement[]} */ ([...this.querySelectorAll('select[data-group-id]')]);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const { target } = event;
    const select = target instanceof HTMLSelectElement && target.dataset.groupId ? target : null;
    if (!select && (!(target instanceof HTMLInputElement) || target.type !== 'radio')) return;
    if (!isCurrent(this, this.#data)) this.#resync();

    const row = select?.closest('[data-zg-row]');
    if (row instanceof HTMLElement) this.#renderGroupRow(row);
    this.#update();
  };

  /** @param {Event & {detail?: {variantId?: string}, promise?: Promise<any>}} event */
  #onProductSelect = (event) => {
    if (!this.#data || !isOwnProductSelect(event, this.dataset.productId)) return;
    if (!isCurrent(this, this.#data)) this.#resync();
    const generation = ++this.#generation;

    // '' when the option combination doesn't exist; Horizon disables add to cart, the rows stay as they are.
    const variantId = String(event.detail?.variantId ?? '');
    if (this.#data?.variant(variantId)) this.#setVariant(variantId);

    whenSettled(event, (result) => {
      const detail = result?.detail;
      if (generation !== this.#generation || !detail || detail.newProduct) return;
      // Horizon has updated the page by now, and may have morphed this element (see #resync).
      this.#resync(detail.resource?.id);
    });
  };

  /**
   * Brings the element back in line after Horizon may have morphed it, which doesn't reconnect it: it re-reads
   * the product data; for the same product it puts the shopper's choice back (a morph resets the radios, selects
   * and hidden input to the server's defaults), for another product it starts from the server-rendered state.
   * Then it re-applies the variant and always announces the result, because a morph also resets zg-price.
   * @param {string | number | null} [variantId] - The variant Horizon settled on, when known.
   */
  #resync(variantId = null) {
    const data = ProductData.from(this);
    if (!data) return;
    const sameProduct = data.productId === this.#data?.productId;
    this.#data = data;

    if (sameProduct) this.#restoreChoice();
    else this.#variantId = this.dataset.variantId ?? '';
    if (variantId != null && data.variant(variantId)) this.#variantId = String(variantId);

    this.#applyVariant();
    this.#update({ url: sameProduct ? true : 'present', emit: false });
    this.#emit(true);
  }

  #restoreChoice() {
    const { radio, selects } = this.#choice;
    for (const select of this.#selects) {
      const value = selects.get(select.dataset.groupId ?? '');
      if (value && [...select.options].some((option) => option.value === value)) select.value = value;
    }
    const checked = radio == null ? undefined : this.#radios.find((input) => input.value === radio);
    if (checked) checked.checked = true;
  }

  /** @param {string} variantId */
  #setVariant(variantId) {
    this.#variantId = variantId;
    this.#applyVariant();
    this.#update();
  }

  /**
   * Shows the plan rows the variant has an allocation for, updates every row's price, and, when the selected plan
   * row disappears, moves the selection to the matching plan row of the new variant (#matchingPlanRadio), else to
   * one-time purchase (or the first remaining plan row).
   */
  #applyVariant() {
    const data = this.#data;
    const variant = data?.variant(this.#variantId);
    if (!data || !variant) return;

    // Where the selection sits among the plan rows shown before this variant change.
    const before = this.#checkedRadio();
    const position = before ? this.#enabledPlanRadios().indexOf(before) : -1;

    for (const row of this.#rows) {
      const radio = row.querySelector('input[type="radio"]');
      if (!(radio instanceof HTMLInputElement)) continue;

      if (radio.hasAttribute('data-zg-one-time')) {
        setText(row.querySelector('[data-zg-row-price]'), data.money(variant.price));
        continue;
      }

      const group = data.group(row.dataset.groupId);
      const allocated = group?.plans.filter((plan) => data.allocation(variant, plan.id)) ?? [];
      row.hidden = allocated.length === 0;
      radio.disabled = row.hidden;

      const select = row.querySelector('select');
      if (select) {
        for (const option of select.options) option.disabled = !data.allocation(variant, option.value);
        const first = allocated[0];
        if (select.selectedOptions[0]?.disabled && first) select.value = String(first.id);
      }
      this.#renderGroupRow(row);
    }

    const checked = this.#checkedRadio();
    if (!checked || checked.disabled) {
      const fallback =
        (checked && this.#matchingPlanRadio(checked, position)) ??
        this.querySelector('input[data-zg-one-time]') ??
        this.#radios.find((radio) => !radio.disabled) ??
        null;
      if (fallback instanceof HTMLInputElement) fallback.checked = true;
      else if (checked) checked.checked = false;
    }
  }

  /** The radios of the plan rows shown and enabled for the current variant, in page order. */
  #enabledPlanRadios() {
    return this.#radios.filter(
      (radio) =>
        !radio.disabled && !radio.hasAttribute('data-zg-one-time') && !radio.closest('[data-zg-row]')?.hidden
    );
  }

  /**
   * The plan row to keep a subscription on when the variant change hid the selected one, as when each pack size has
   * its own selling plan groups: the shown row whose group has the same name ("Subscribe · every 90 days"), else the
   * row at the same position among the shown plan rows. Null when there is none: the caller falls back to one-time.
   * @param {HTMLInputElement} previous - The checked radio, now disabled.
   * @param {number} position - Its index among the plan rows shown before the change, or -1.
   * @returns {HTMLInputElement | null}
   */
  #matchingPlanRadio(previous, position) {
    const data = this.#data;
    const enabled = this.#enabledPlanRadios();
    const nameOf = (/** @type {HTMLInputElement} */ radio) =>
      String(data?.group(radio.dataset.groupId)?.name ?? '').trim();
    const name = nameOf(previous);
    const sameName = name ? enabled.find((radio) => nameOf(radio) === name) : undefined;
    return sameName ?? enabled[position] ?? null;
  }

  /**
   * The plan a group row stands for: its frequency select, else the group's first plan the variant has.
   * @param {HTMLElement} row
   * @returns {string | null}
   */
  #planFor(row) {
    const select = row.querySelector('select');
    if (select) return select.value || null;

    const data = this.#data;
    const group = data?.group(row.dataset.groupId);
    if (!data || !group) return null;
    const variant = data.variant(this.#variantId);
    const plan = group.plans.find((item) => data.allocation(variant, item.id)) ?? group.plans[0];
    return plan ? String(plan.id) : null;
  }

  /** @param {HTMLElement} row */
  #renderGroupRow(row) {
    const data = this.#data;
    if (!data) return;
    const planId = this.#planFor(row);
    const variant = data.variant(this.#variantId);
    const allocation = data.allocation(variant, planId);
    setText(row.querySelector('[data-zg-row-price]'), allocation ? data.money(allocation.price) : '');

    // The saving: the struck price and the badge, both blank and hidden without one (a hidden badge still counts in
    // the radio's aria-labelledby, so it is emptied too).
    const pricing = allocation && variant && this.dataset.showSavings === 'true' ? data.pricing(variant, planId) : null;
    const compareAt = pricing?.compareAt ?? null;
    const compare = row.querySelector('[data-zg-row-compare]');
    setText(compare, compareAt == null ? '' : data.money(compareAt));
    if (compare instanceof HTMLElement) compare.hidden = compareAt == null;
    const save = row.querySelector('[data-zg-row-save]');
    const saveLabel = pricing ? saveText(this.dataset.saveTemplate, savePercent(pricing.price, compareAt)) : '';
    setText(save, saveLabel);
    if (save instanceof HTMLElement) save.hidden = !saveLabel;

    const planText = planTextFor(row, variant);
    if (planText) setText(row.querySelector('[data-zg-plan-title]'), planText.title);

    // Without a merchant description, the row describes the plan picked in its frequency select.
    const description = row.querySelector('[data-zg-plan-description]');
    if (description instanceof HTMLElement) {
      const text = (planText?.description ?? data.plan(planId)?.description ?? '').trim();
      setText(description, text);
      description.hidden = !text;
    }
  }

  #checkedRadio() {
    return this.#radios.find((radio) => radio.checked) ?? null;
  }

  /** The selected plan id from the radios and selects, or null for one-time purchase. */
  #selectedPlanId() {
    const checked = this.#checkedRadio();
    const row = checked?.closest('[data-zg-row]');
    if (!checked || checked.hasAttribute('data-zg-one-time') || !(row instanceof HTMLElement)) return null;
    return this.#planFor(row);
  }

  /**
   * Brings the frequency selects, the hidden input and the URL in line with the selection, then tells
   * listeners if anything changed.
   * @param {{url?: boolean | 'present', emit?: boolean}} [options] - `url: 'present'` only corrects a
   *   selling_plan parameter that is already in the URL.
   */
  #update({ url = true, emit = true } = {}) {
    const checked = this.#checkedRadio();
    const planId = this.#selectedPlanId();

    for (const row of this.#rows) {
      const frequency = row.querySelector('[data-zg-frequency]');
      if (frequency instanceof HTMLElement) frequency.hidden = !checked || !row.contains(checked);
    }

    const input = this.#input;
    if (input) {
      input.value = planId ?? '';
      input.disabled = !planId;
    }

    this.#choice = {
      radio: checked?.value ?? null,
      selects: new Map(this.#selects.map((select) => [select.dataset.groupId ?? '', select.value])),
    };

    if (url) this.#updateUrl(planId, url === 'present');
    if (emit) this.#emit(false);
  }

  /**
   * @param {string | null} planId
   * @param {boolean} onlyIfPresent
   */
  #updateUrl(planId, onlyIfPresent) {
    if (!this.hasAttribute('data-sync-url') || this.closest('dialog, product-card')) return;
    if (/** @type {any} */ (window).Shopify?.designMode) return;

    const url = new URL(window.location.href);
    if (onlyIfPresent && !url.searchParams.has('selling_plan')) return;
    if (planId) url.searchParams.set('selling_plan', planId);
    else url.searchParams.delete('selling_plan');
    if (url.href !== window.location.href) history.replaceState(history.state, '', url.toString());
  }

  /** @param {boolean} force */
  #emit(force) {
    if (!this.isConnected) return;
    const sellingPlanId = this.sellingPlanId;
    const state = `${this.#variantId}:${sellingPlanId ?? ''}`;
    if (!force && state === this.#emitted) return;
    this.#emitted = state;
    this.dispatchEvent(
      new CustomEvent(PURCHASE_OPTION_CHANGE, {
        bubbles: true,
        detail: { variantId: this.#variantId, sellingPlanId },
      })
    );
  }
}

class ZgPrice extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {ProductData | null} */
  #data = null;
  #variantId = '';
  #available = false;
  /** @type {string | null} */
  #sellingPlanId = null;
  #generation = 0;
  /** @type {HTMLElement | null} */
  #totalTarget = null;

  connectedCallback() {
    if (!this.#load()) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const scope = scopeOf(this);

    scope.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    scope.addEventListener(PURCHASE_OPTION_CHANGE, this.#onPurchaseOptionChange, { signal });
    scope.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity, { signal });
    scope.addEventListener('input', this.#onQuantity, { signal });
    scope.addEventListener('change', this.#onQuantity, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#totalTarget?.style.removeProperty('--zg-atc-total');
    this.#totalTarget = null;
  }

  /**
   * Reads the product data: on connect, and again after Horizon may have morphed this element, which doesn't
   * reconnect it. For another product (combined listing, reused quick add dialog) the variant and plan start
   * from the server-rendered state; for the same product they're kept, since the events below keep them current.
   * @returns {boolean} Whether there is product data.
   */
  #load() {
    const data = ProductData.from(this);
    if (!data) return false;
    const sameProduct = data.productId === this.#data?.productId;
    this.#data = data;
    if (sameProduct) return true;

    this.#variantId = this.dataset.variantId ?? '';
    this.#available = data.variant(this.#variantId)?.available ?? false;
    const productId = CSS.escape(this.dataset.productId ?? '');
    const options = scopeOf(this).querySelector(`zg-purchase-options[data-product-id="${productId}"]`);
    if (options) {
      const input = options.querySelector('input[data-zg-selling-plan]');
      this.#sellingPlanId = input instanceof HTMLInputElement && !input.disabled && input.value ? input.value : null;
    } else {
      this.#sellingPlanId = this.dataset.sellingPlanId || null;
    }
    return true;
  }

  /** Re-reads the data when a morph has turned this element into another product's. */
  #ensureCurrent() {
    if (!isCurrent(this, this.#data)) this.#load();
  }

  /** @param {Event & {detail?: {variantId?: string}, promise?: Promise<any>}} event */
  #onProductSelect = (event) => {
    if (!this.#data || !isOwnProductSelect(event, this.dataset.productId)) return;
    this.#ensureCurrent();
    const generation = ++this.#generation;

    const variantId = String(event.detail?.variantId ?? '');
    if (variantId && this.#data.variant(variantId)) {
      this.#variantId = variantId;
      this.#available = this.#data.variant(variantId)?.available ?? false;
    } else {
      // The combination doesn't exist: keep the last price on screen, drop the total.
      this.#available = false;
    }
    this.#render();

    whenSettled(event, (result) => {
      const detail = result?.detail;
      if (generation !== this.#generation || !detail || detail.newProduct) return;
      // Horizon has updated the page by now, and may have morphed this element (see #load and #render).
      this.#load();
      const { resource } = detail;
      if (resource?.id != null && this.#data?.variant(resource.id)) {
        this.#variantId = String(resource.id);
        this.#available = resource.available !== false;
      } else if (resource === null) {
        this.#available = false;
      }
      this.#render();
      // Horizon snaps the quantity to the new variant's rules without an event; read it once that has run.
      requestAnimationFrame(() => this.#render());
    });
  };

  /** @param {Event & {detail?: {sellingPlanId?: string | number | null}}} event */
  #onPurchaseOptionChange = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLElement) || target.dataset.productId !== this.dataset.productId) return;
    this.#ensureCurrent();
    const sellingPlanId = event.detail?.sellingPlanId;
    this.#sellingPlanId = sellingPlanId == null || sellingPlanId === '' ? null : String(sellingPlanId);
    this.#render();
  };

  /** @param {Event} event */
  #onQuantity = (event) => {
    if (event.type !== ThemeEvents.quantitySelectorUpdate) {
      const { target } = event;
      if (!(target instanceof HTMLInputElement) || target.name !== 'quantity') return;
    }
    this.#render();
  };

  /**
   * After an add, Horizon may clamp the quantity to what's left; re-read it. Errors are Horizon's to report.
   * @param {Event & {promise?: Promise<any>}} event
   */
  #onCartUpdate = (event) => {
    event.promise?.then(() => requestAnimationFrame(() => this.#render())).catch(() => {});
  };

  #quantity() {
    const formId = this.dataset.formId;
    const scope = scopeOf(this);
    const form = formId ? scope.querySelector(`#${CSS.escape(formId)}`) : null;
    const input = form?.querySelector('input[name="quantity"]');
    const value = input instanceof HTMLInputElement ? Number.parseInt(input.value, 10) : NaN;
    if (value > 0) return value;
    const fallback = Number(form?.closest('product-form-component')?.getAttribute('data-quantity-default'));
    return fallback > 0 ? fallback : 1;
  }

  #render() {
    this.#ensureCurrent();

    // Before the script ran, and again after a morph, CSS shows whichever server-rendered row matches the
    // checked purchase option. The script keeps one row and updates it.
    const [row, ...extraRows] = this.querySelectorAll('.zg-price__row');
    for (const extra of extraRows) extra.remove();
    row?.removeAttribute('data-zg-view');

    const data = this.#data;
    const variant = data?.variant(this.#variantId);
    if (!data || !variant || !row) {
      this.#setTotal('');
      return;
    }

    const { price, compareAt, isPlan } = data.pricing(variant, this.#sellingPlanId);
    setText(row.querySelector('[data-zg-price]'), data.money(price));

    const compare = row.querySelector('[data-zg-compare]');
    const saleLabel = row.querySelector('[data-zg-sale-label]');
    setText(row.querySelector('[data-zg-compare-amount]'), compareAt == null ? '' : data.money(compareAt));
    if (compare instanceof HTMLElement) compare.hidden = compareAt == null;
    if (saleLabel instanceof HTMLElement) saleLabel.hidden = compareAt == null;

    // "Save 10%" after the struck price: plans only, never a one-time compare-at sale.
    const save = row.querySelector('[data-zg-save]');
    const saveLabel =
      isPlan && this.dataset.showPlanSavings === 'true'
        ? saveText(this.dataset.saveTemplate, savePercent(price, compareAt))
        : '';
    setText(save, saveLabel);
    if (save instanceof HTMLElement) save.hidden = !saveLabel;

    const unitLabel = this.dataset.unitLabel ?? '';
    const units = Number(variant.units);
    const unitText = unitLabel && units > 0 ? `${data.money(Math.round(price / units))} ${unitLabel}` : '';
    // The price_suffix_by_units line for the variant's item count replaces the price suffix.
    const priceSuffix =
      textForUnits(parseJson(this.dataset.priceSuffixByUnits), variant.units) || (this.dataset.priceSuffix ?? '');
    const suffixText = [priceSuffix, unitText].filter(Boolean).join(' · ');
    const suffix = row.querySelector('[data-zg-suffix]');
    setText(suffix, suffixText);
    if (suffix instanceof HTMLElement) suffix.hidden = !suffixText;

    const comparisonText = this.dataset.showComparison === 'true' ? (variant.comparison ?? '').trim() : '';
    const comparison = this.querySelector('[data-zg-comparison]');
    setText(comparison, comparisonText);
    if (comparison instanceof HTMLElement) comparison.hidden = !comparisonText;

    this.#setTotal(this.#available ? ` · ${data.money(price * this.#quantity())}` : '');
  }

  /** @param {string} text */
  #setTotal(text) {
    const target = this.closest('.zg-product-information');
    if (!(target instanceof HTMLElement)) return;
    this.#totalTarget = target;
    const value = cssString(text);
    if (target.style.getPropertyValue('--zg-atc-total') !== value) target.style.setProperty('--zg-atc-total', value);
  }
}

// zg-price first: on a fresh page it is listening before zg-purchase-options announces its state.
if (!customElements.get('zg-price')) customElements.define('zg-price', ZgPrice);
if (!customElements.get('zg-purchase-options')) customElements.define('zg-purchase-options', ZgPurchaseOptions);
